/**
 * The rejection-reason vocabulary — SIMS-8482 / 8490 / 8498.
 *
 * 🔴 Lives in `domain`, not in the component that renders it. It was exported
 * from `components/RejectionReasonsSection` until 2026-09-18, which made
 * `domain/event.interface.ts` import from a UI component — a domain type
 * depending on a view. Services, pages and the domain layer all need these, so
 * the component was acting as a types module by accident.
 */

/**
 * Where a reason came from.
 *
 * Derived from origin, never authored: a reason's tier follows from where it was
 * created and whether it was later promoted (SIMS-8498 AC4), so nothing lets an
 * author pick one. The code records where it was BORN, not where it lives — an
 * `EVT_` code survives promotion to ACCOUNT — which is why the tier is carried
 * as its own field rather than decoded from the code.
 */
export enum RejectionReasonTier {
	PLATFORM = "PLATFORM",
	ACCOUNT = "ACCOUNT",
	EVENT = "EVENT",
}

/**
 * The prefix an event-only code is issued with (SIMS-8490 AC5).
 *
 * 🔴 A code records where a reason was BORN, not where it lives now: a reason
 * promoted to the account keeps its `EVT_` prefix (SIMS-8498 AC4), so a tier
 * cannot be decoded from a code. Safe because every recorded decision freezes
 * its own tier.
 */
export const EVENT_REASON_CODE_PREFIX = "EVT_";

export interface EventRejectionReason {
	id: string;
	title: string;
	description?: string;
	participantCopy: string;
	/** The higher tier's wording, kept so AC4 can show it alongside and restore it. */
	originalParticipantCopy?: string;
	suggestedFix?: string;
	requiresNote: boolean;
	tier: RejectionReasonTier;
}
